/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import type { MutableRefObject } from 'react';

import { OverflowCarousel } from '@coursera-web/cds-labs';

import Imgix from 'js/components/Imgix';

import { Typography2 } from '@coursera/cds-core';
import { useVisibilityTracker } from '@coursera/event-pulse/react';

import type { Maybe } from 'bundles/premium-hub/__generated__/baseContentfulTypes';
import type { TestimonialVisualConsistencyFragment } from 'bundles/premium-hub/__generated__/contentfulTypes';
import Container from 'bundles/premium-hub/components/shared/Container';
import { useModuleSectionEventTrackingData } from 'bundles/premium-hub/hooks/useModuleSectionEventTrackingData';

import _t from 'i18n!nls/premium-hub';

export type TestimonialsModuleProps = {
  testimonialsHeader: Maybe<string>;
  testimonials: Maybe<Array<TestimonialVisualConsistencyFragment | null>>;
};

type TestimonialCardProps = {
  testimonial: TestimonialVisualConsistencyFragment;
  index: number;
};

const AVATAR_SIZE = 44;

const styles = {
  moduleContainer: css`
    margin-top: calc(var(--cds-spacing-100) * 5);
    margin-bottom: calc(var(--cds-spacing-100) * 5);
  `,
  heading: css`
    margin-bottom: var(--cds-spacing-150);
  `,
  // Shows four cards per view (never narrower than 240px); the rest scroll into view.
  cardList: css`
    display: grid;
    flex: none;
    grid-auto-flow: column;
    grid-auto-columns: max(240px, calc((100% - 3 * var(--cds-spacing-200)) / 4));
    gap: var(--cds-spacing-200);
    box-sizing: border-box;
    width: 100%;
  `,
  card: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-100);
    box-sizing: border-box;

    /* Fills the carousel's list item, which the grid row stretches to the tallest card. */
    height: 100%;
    min-height: 211px;
    padding: var(--cds-spacing-200);
    overflow: hidden;
    background-color: var(--cds-color-neutral-background-primary);
    border: 1px solid var(--cds-color-grey-100);
    border-radius: var(--cds-border-radius-300);
  `,
  author: css`
    display: flex;
    align-items: center;
    gap: var(--cds-spacing-200);
  `,
  avatar: css`
    flex-shrink: 0;
    width: ${AVATAR_SIZE}px;
    height: ${AVATAR_SIZE}px;
    border-radius: 50%;
    object-fit: cover;
  `,
  avatarInitial: css`
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: var(--cds-color-neutral-background-primary-weak);
  `,
  authorText: css`
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
    word-break: break-word;
  `,
  authorSubtitle: css`
    color: var(--cds-color-neutral-primary-weak);
  `,
  quote: css`
    color: var(--cds-color-neutral-primary-weak);
    word-break: break-word;
  `,
};

const TestimonialCard = ({ testimonial, index }: TestimonialCardProps) => {
  const { authorName, authorTitle, text, image, videoPlaceholderImage } = testimonial;
  const avatarImage = image ?? videoPlaceholderImage;
  const cardRef: MutableRefObject<HTMLDivElement | null> = useVisibilityTracker(
    'view_testimonial_card',
    {
      testimonialCard: { author: authorName ?? '', text: text ?? '', index },
    },
    [],
    { fullyVisible: true }
  );

  return (
    <div css={styles.card} ref={cardRef} data-testid="testimonial-card">
      <div css={styles.author}>
        {avatarImage?.url ? (
          <Imgix
            src={avatarImage.url}
            alt={avatarImage.description ?? ''}
            css={styles.avatar}
            loading="lazy"
            imgParams={{ auto: 'format,compress', fit: 'crop', width: AVATAR_SIZE * 2, height: AVATAR_SIZE * 2 }}
          />
        ) : (
          <div css={[styles.avatar, styles.avatarInitial]} aria-hidden>
            <Typography2 variant="subtitleMedium" component="span">
              {authorName?.charAt(0).toUpperCase()}
            </Typography2>
          </div>
        )}
        <div css={styles.authorText}>
          <Typography2 variant="subtitleMedium" component="p">
            {authorName}
          </Typography2>
          {authorTitle && (
            <Typography2 variant="bodySecondary" component="p" css={styles.authorSubtitle}>
              {authorTitle}
            </Typography2>
          )}
        </div>
      </div>
      <Typography2 variant="bodyPrimary" component="p" css={styles.quote}>
        {`"${text}"`}
      </Typography2>
    </div>
  );
};

const TestimonialsModule = ({ testimonialsHeader, testimonials }: TestimonialsModuleProps) => {
  const { index: sectionIndex } = useModuleSectionEventTrackingData();
  const sectionRef: MutableRefObject<HTMLDivElement | null> = useVisibilityTracker(
    'view_page_section',
    {
      pageSection: { sectionName: 'learner_testimonials', index: sectionIndex },
    },
    [],
    { fullyVisible: true }
  );

  const testimonialsToDisplay = (testimonials ?? []).filter(
    (testimonial): testimonial is TestimonialVisualConsistencyFragment => !!testimonial?.text
  );

  if (!testimonialsToDisplay.length) {
    return null;
  }

  return (
    <Container css={styles.moduleContainer}>
      <div ref={sectionRef} data-testid="testimonials-module">
        <Typography2 variant="subtitleLarge" component="h2" css={styles.heading}>
          {testimonialsHeader ?? _t('Hear why students enjoy learning on Coursera')}
        </Typography2>
        <OverflowCarousel css={styles.cardList}>
          {testimonialsToDisplay.map((testimonial, index) => (
            <TestimonialCard
              key={`${testimonial.authorName}-${testimonial.authorTitle}-${testimonial.authorOrganization}`}
              testimonial={testimonial}
              index={index}
            />
          ))}
        </OverflowCarousel>
      </div>
    </Container>
  );
};

export default TestimonialsModule;
